<script setup lang="ts">
import { computed, inject, onMounted, ref, shallowRef, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";

import PlotlyWidget, { type PlotlyWidgetSpec } from "@/components/datascience/PlotlyWidget.vue";
import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import SideDrawer from "@/components/ui/SideDrawer.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { ReportType, type EstimatorReportSummaryDto, type ReportGroupDto } from "@/dto";
import { raiseErrorKey } from "@/injectionKeys";
import GridLayoutWithSidebar from "@/layouts/GridLayoutWithSidebar.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { getColorInGradient, intersect, remap } from "@/services/utils";
import { useDatasourceStore } from "@/stores/datasource";
import DatasourceToggle from "@/views/DatasourceToggle.vue";
import ProjectDetailViewExperimentsSearchGroupSelector from "@/views/projects/ProjectDetailViewExperimentsSearchGroupSelector.vue";
import ProjectDetailViewExperimentsSearchResult from "@/views/projects/ProjectDetailViewExperimentsSearchResult.vue";
import PutReportTutorial from "@/views/PutReportTutorial.vue";

import gradient from "@/assets/images/copper_gradient.png";

interface PlotDimension {
  range: [number, number];
  label: string;
  name: string;
  values: number[];
  position: number;
  greater_is_better: boolean | null;
}

const { reportType } = defineProps<{
  reportType: ReportType;
}>();

const api = useApi();
const route = useRoute();
const datasource = useDatasourceStore();

const raiseError = inject(raiseErrorKey);

const isLoadingGroups = ref(true);
const isLoadingPlot = ref(true);
const isLoadingSearch = ref(true);
const estimatorReportsGroups = shallowRef<ReportGroupDto[]>([]);
const plotReports = shallowRef<EstimatorReportSummaryDto[]>([]);
const searchReports = shallowRef<EstimatorReportSummaryDto[]>([]);
const searchReportsColors = ref<string[]>([]);
const selectedDataset = ref<string>("");
const selectedEstimator = ref<string>("");
const dimensions = ref<PlotDimension[]>([]);
const colorByDimensionIndex = ref(-1);
const plotSpec = ref<PlotlyWidgetSpec>();

const tutorialDrawer = useTemplateRef<InstanceType<typeof SideDrawer>>("tutorialDrawer");

const gradientStops: (string | number)[][] = [];

const workspacePublicId = computed(() => route.params.workspacePublicId as string);
const projectName = computed(() => route.params.projectName as string);

async function fetchReportGroups() {
  isLoadingGroups.value = true;
  const results = await api.listReportGroups(
    workspacePublicId.value,
    projectName.value,
    reportType
  );
  if (results) {
    estimatorReportsGroups.value = results;
    isLoadingGroups.value = false;
    const firstGroup = results[0];
    if (firstGroup) {
      onGroupSelected(firstGroup.dataset);
    } else {
      isLoadingPlot.value = false;
      isLoadingSearch.value = false;
    }
  } else {
    raiseError?.(404);
  }
}

async function _fetchFilteredReports(dimensions?: { constraintrange: Array<any>; name: string }[]) {
  const dataset = selectedDataset.value;
  if (dataset.length > 0) {
    const searchParams = new URLSearchParams();

    const estimator = selectedEstimator.value;
    if (estimator.length > 0) {
      searchParams.append("estimator_class_name", estimator);
    }
    searchParams.append("data_source", datasource.sourceName);
    if (dimensions) {
      dimensions.forEach(({ constraintrange, name }) => {
        // if the user has selected one range
        // plotly sends an array that contains 2 values ([1.6893174913194444, 2.06263274444239])
        // else plotly sends and array of array...
        // ([[0.7397982605502139, 1.0481891218256905],
        // [1.6893174913194444, 2.06263274444239]])
        let ranges = constraintrange;
        if (!Array.isArray(constraintrange[0])) {
          ranges = [constraintrange];
        }
        ranges.forEach((r) => {
          const [start, end] = r;
          searchParams.append(`metrics__${name}__gte`, start);
          searchParams.append(`metrics__${name}__lte`, end);
        });
      });
    }

    switch (reportType) {
      case ReportType.EstimatorReport: {
        return await api.filterEstimatorReports(
          workspacePublicId.value,
          projectName.value,
          dataset,
          searchParams
        );
      }
      case ReportType.CrossValidationReport: {
        return await api.filterCrossValidationReports(
          workspacePublicId.value,
          projectName.value,
          dataset,
          searchParams
        );
      }
    }
  }
  return [];
}

async function fetchPlotReports(dimensions?: { constraintrange: Array<any>; name: string }[]) {
  isLoadingPlot.value = true;
  isLoadingSearch.value = true;
  plotReports.value = await _fetchFilteredReports(dimensions);
  isLoadingPlot.value = false;

  colorByDimensionIndex.value = -1;
  await fechSearchResults(true, dimensions);
  await sortResults();
}

async function fechSearchResults(
  triggerPlotUpdate: boolean,
  dimensions?: { constraintrange: Array<any>; name: string }[]
) {
  isLoadingSearch.value = true;
  searchReports.value = await _fetchFilteredReports(dimensions);
  isLoadingSearch.value = false;

  if (triggerPlotUpdate) {
    updatePlot();
  }
}

function updatePlot() {
  const newDataSources = new Set<string>();
  for (const report of plotReports.value) {
    for (const metric of report.metrics) {
      if (metric.data_source !== null) {
        newDataSources.add(metric.data_source);
      }
    }
  }

  // build dimension map
  const dimensionsMap = new Map<string, PlotDimension>();
  for (const report of plotReports.value) {
    for (const metric of report.metrics) {
      if (
        metric.position !== null &&
        (metric.data_source === datasource.sourceName || metric.data_source === null)
      ) {
        if (dimensionsMap.has(metric.name)) {
          const d = dimensionsMap.get(metric.name)!;
          d.values.push(metric.value);
          d.range[0] = Math.min(d.range[0], metric.value);
          d.range[1] = Math.max(d.range[1], metric.value);
        } else {
          dimensionsMap.set(metric.name, {
            range: [metric.value, metric.value],
            label: metric.verbose_name.length > 0 ? metric.verbose_name : metric.name,
            name: metric.name,
            values: [metric.value],
            position: metric.position,
            greater_is_better: metric.greater_is_better,
          });
        }
      }
    }
  }
  // check that the dimension map contains only metrics that are available in all results
  const allMetrics = plotReports.value.flatMap((r) =>
    r.metrics.filter((m) => m.position !== null).map((m) => m.name)
  );
  const commonMetricsNames = [...plotReports.value].reduce(
    (accum, report) =>
      intersect(
        accum,
        report.metrics.map((m) => m.name)
      ),
    allMetrics
  );

  dimensions.value = [...dimensionsMap.values()]
    .filter((d) => commonMetricsNames.includes(d.name))
    .sort((a, b) => a.position - b.position)
    .map((d) => ({ ...d, label: d.label.replace(" - MEAN", "") }));
  if (colorByDimensionIndex.value === -1) {
    colorByDimensionIndex.value = dimensions.value.length - 1;
  }

  const longestDimensionNameLength = Math.max(...dimensions.value.map((d) => d.label.length));
  const padding = 15;
  const fontSize = 14;
  const margin = {
    t: fontSize * 2 + padding,
    l: longestDimensionNameLength * fontSize * 0.6 * 0.5,
    b: fontSize + padding,
    r: 0,
  };
  const spec = {
    data: [
      {
        type: "parcoords",
        line: {
          color: [1],
          colorbar: { title: { text: "" }, tickfont: { size: 8 } },
          colorscale: gradientStops,
          showscale: true,
          reversescale: false,
          width: 3,
        },
        dimensions: dimensions.value,
      },
    ],
    layout: {
      margin,
      font: {
        family: "GeistMono, monospace",
        size: fontSize,
      },
    },
    frames: [],
  };
  const colorDimension = dimensions.value[colorByDimensionIndex.value];
  if (colorDimension !== undefined) {
    const inverseColorScale = colorDimension.greater_is_better === false;
    const first = spec.data[0];
    if (first) {
      first.line = {
        ...first.line,
        color: colorDimension.values,
        colorbar: { ...first.line.colorbar, title: { text: colorDimension.label } },
        reversescale: inverseColorScale,
      };
    }
  }
  plotSpec.value = spec as PlotlyWidgetSpec;
}

async function onGroupSelected(dataset: string, estimator?: string) {
  selectedDataset.value = dataset;
  selectedEstimator.value = estimator ?? "";
  colorByDimensionIndex.value = -1;
  await fetchPlotReports();
}

async function onPlotDataChange(data: any) {
  const dimensions = data[0]["dimensions"];
  const constrainedDimensions = dimensions.filter((d: any) => "constraintrange" in d);
  await fechSearchResults(false, constrainedDimensions);
}

async function sortResults() {
  const colorBy = dimensions.value[colorByDimensionIndex.value];
  if (colorBy) {
    const source = datasource.isTestDatasource ? "test" : "train";

    const getCriterion = (r: EstimatorReportSummaryDto) =>
      r.metrics.find((m) => m.name === colorBy.name && m.data_source === source)?.value ?? 0;

    const results = [...searchReports.value].sort((a, b) => getCriterion(a) - getCriterion(b));
    if (colorBy.greater_is_better) {
      results.reverse();
    }
    searchReports.value = results;

    const colors: string[] = [];
    for (const report of searchReports.value) {
      const value = getCriterion(report);
      const range = dimensions.value.find((d) => d.name == colorBy.name)?.range;
      if (range) {
        let ratio = 0.5;
        if (range[0] != range[1]) {
          if (colorBy.greater_is_better) {
            ratio = remap(value, range[0], range[1], 0, 1);
          } else {
            ratio = remap(value, range[0], range[1], 1, 0);
          }
        }
        const color = await getColorInGradient(ratio, gradient);
        colors.push(color);
      }
    }
    searchReportsColors.value = colors;
  }
}

function openTutorialDrawer() {
  tutorialDrawer.value?.open();
}

onMounted(async () => {
  const stops = await Promise.all(
    Array.from({ length: 10 }, (_, i) => i / 9).map(async (i) => [
      i,
      await getColorInGradient(i, gradient),
    ])
  );
  gradientStops.push(...stops);
  await fetchReportGroups();
});

watch(colorByDimensionIndex, async () => {
  updatePlot();
  await sortResults();
});

watch(
  () => datasource.isTestDatasource,
  async () => {
    await fechSearchResults(true);
    await sortResults();
  }
);
</script>

<template>
  <div class="report-search">
    <div class="loader" v-if="isLoadingGroups">
      <SkoreSpinner />
    </div>

    <div class="tutorial" v-else-if="!isLoadingGroups && estimatorReportsGroups.length === 0">
      <header>
        <div class="icon">
          <i
            class="icon"
            :class="{
              'icon-cube': reportType === ReportType.EstimatorReport,
              'icon-playground': reportType === ReportType.CrossValidationReport,
            }"
          />
        </div>

        <div class="text-xl-medium">
          No
          {{ reportType === ReportType.EstimatorReport ? "estimators" : "cross-validations" }}
          reports yet
        </div>

        <div class="subtitle text-base-regular">
          Skore reports are shown here once pushed through your code.
          <br />
          <a href="https://docs.skore.probabl.ai/" target="_blank">Get started</a>
          with Skore.
        </div>
      </header>

      <div class="actions">
        <SimpleButton
          :label="`One step away from adding ${reportType === ReportType.EstimatorReport ? 'an estimator' : 'a cross-validation'} report`"
          icon-right="icon-ascending-arrow"
          @click="openTutorialDrawer"
        />
      </div>
    </div>

    <GridLayoutWithSidebar
      v-else
      :top-min-height="150"
      :localStorageKey="`${reportType === ReportType.EstimatorReport ? 'estimator' : 'cross-validation'}-grid-layout-top-height`"
    >
      <template #sidebarTitle>
        <div class="wrapper">
          <div class="title text-base-regular"><i class="icon icon-branch" /> Experiment group</div>
        </div>
      </template>
      <template #contentTitle>
        <div class="plot-settings">
          <div>
            Color by:
            <DropdownButton
              :label="dimensions[colorByDimensionIndex]?.label"
              icon-right="icon-chevron-down"
            >
              <DropdownButtonItem
                v-for="(dimension, index) in dimensions"
                :key="dimension.label"
                :label="dimension.label"
                @click="colorByDimensionIndex = index"
              />
            </DropdownButton>
            <div class="legend">
              <div v-if="dimensions[colorByDimensionIndex]?.greater_is_better">
                <i class="icon icon-ascending-arrow" />Higher is better
              </div>
              <div v-else><i class="icon icon-descending-arrow" />Lower is better</div>
            </div>
          </div>
          <div>
            <DatasourceToggle variant="toggle" />
          </div>
        </div>
      </template>

      <template #sidebar>
        <Transition name="fade" mode="out-in">
          <div v-if="isLoadingGroups" class="loading">
            <SkoreSpinner />
          </div>

          <div class="tree" v-else-if="estimatorReportsGroups.length > 0">
            <ProjectDetailViewExperimentsSearchGroupSelector
              :title="projectName"
              :groups="estimatorReportsGroups"
              @selected="onGroupSelected"
            />
          </div>
        </Transition>
      </template>

      <template #top>
        <Transition name="fade" mode="out-in">
          <div v-if="isLoadingPlot" class="loading">
            <SkoreSpinner />
          </div>

          <div
            v-else-if="estimatorReportsGroups.length > 0"
            ref="plotContainer"
            class="plot-container"
          >
            <div class="plot-scaler">
              <PlotlyWidget
                v-if="plotSpec !== undefined"
                :spec="plotSpec"
                :display-mode-bar="false"
                @data-change="onPlotDataChange"
              />
            </div>
          </div>

          <div v-else class="empty">
            <div class="title text-2xl-medium">No items</div>
            <div class="subtitle text-base-regular">
              No item selected. Try to first select an item in the experiment group sidebar.
            </div>
          </div>
        </Transition>
      </template>

      <template #bottom>
        <Transition name="fade" mode="out-in">
          <div v-if="isLoadingSearch" class="loading">
            <SkoreSpinner />
          </div>

          <div class="results" v-else-if="estimatorReportsGroups.length > 0">
            <div class="results-header">
              <div class="results-count text-base-semibold">
                {{ searchReports.length }} result{{ searchReports.length > 1 ? "s" : "" }}
              </div>

              <RouterLink
                v-if="reportType === ReportType.EstimatorReport"
                class="skore-button primary"
                :to="{
                  name: RouteNames.ComparisonBuilder,
                  params: { workspacePublicId, projectName },
                  query: { reportId: searchReports.map((e) => e.id) },
                }"
              >
                <i class="icon left icon-arrow-loop" />
                <span class="label">Compare</span>
              </RouterLink>
            </div>

            <div class="results-cards">
              <ProjectDetailViewExperimentsSearchResult
                v-for="(report, index) in searchReports"
                :key="report.id"
                :report="report"
                :style="{ '--color': searchReportsColors[index] }"
              />
            </div>
          </div>

          <div v-else class="empty">
            <div class="title text-2xl-medium">No experiments filtered</div>
            <div class="subtitle text-base-regular">No experiment found.</div>
          </div>
        </Transition>
      </template>
    </GridLayoutWithSidebar>
  </div>

  <Teleport to="body">
    <SideDrawer ref="tutorialDrawer" content-width="60dvw">
      <template #title>Get started using a local IDE or notebook</template>

      <PutReportTutorial
        :workspace-public-id="workspacePublicId"
        :project-name="projectName"
        :report-type="reportType"
      />
    </SideDrawer>
  </Teleport>
</template>

<style scoped>
.tutorial {
  display: flex;
  height: 100%;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: var(--numbers-32);
  gap: var(--numbers-32);
  text-align: center;

  header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--numbers-16);

    .subtitle {
      color: var(--content-secondary);
    }
  }

  .icon {
    margin-bottom: var(--numbers-12);

    i {
      padding: var(--numbers-8);
      border-radius: var(--numbers-6);
      background-color: var(--background-tertiary);
      color: var(--content-brand);
      vertical-align: middle;
    }
  }
}

.loading {
  padding: var(--numbers-20);
  text-align: center;
}

.report-search {
  height: 100%;

  .grid-layout-with-sidebar {
    height: 100%;
    background-color: var(--background-primary);
  }

  .tree {
    padding: var(--numbers-8);
  }

  .title {
    .wrapper {
      display: flex;
      height: 100%;
      flex-direction: row;
      align-items: center;

      .icon {
        color: var(--content-brand);
        font-size: var(--numbers-20);
        vertical-align: text-top;
      }
    }
  }

  .plot-settings {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: var(--numbers-6);
    }

    .legend {
      color: var(--content-tertiary);

      i {
        padding-right: var(--numbers-6);
        vertical-align: middle;
      }
    }
  }

  .plot-container {
    height: 100%;

    .plot-scaler {
      /* help readability by scaling up the plot */

      width: 75%;
      height: 75%;
      transform: scale(1.33);
      transform-origin: top left;
    }
  }

  .results {
    --nb-columns: 1;

    container-name: results;
    container-type: inline-size;

    .results-header {
      position: sticky;
      top: 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: var(--numbers-8);
      background-color: var(--background-primary);

      .results-count {
        color: var(--content-primary);
      }
    }

    .results-cards {
      display: grid;
      padding: var(--numbers-8);
      gap: var(--numbers-8);
      grid-template-columns: repeat(var(--nb-columns), 1fr);

      :deep(.header .icon) {
        color: var(--color);
      }
    }
  }

  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;

    .subtitle {
      color: var(--color-text-secondary);
    }
  }

  .sidebar .empty {
    height: calc(var(--content-height) - var(--numbers-20));
  }

  .top .empty {
    height: 100%;
  }

  .bottom .empty {
    margin-top: var(--numbers-56);
  }

  .top .empty,
  .bottom .empty {
    > div {
      max-width: 40%;
    }
  }
}

@media screen and (width >= 1025px) {
  .report-search .results {
    --nb-columns: 2;
  }
}

@supports (animation-timeline: scroll()) {
  .results-header {
    animation: scroll-shadow linear both;
    animation-range: 0 calc(var(--numbers-32) + (var(--numbers-8) * 2));
    animation-timeline: scroll();
  }

  @keyframes scroll-shadow {
    from {
      box-shadow: none;
    }

    to {
      box-shadow:
        0 4px 8px -2px rgb(16 24 40 / 10%),
        0 2px 4px -2px rgb(16 24 40 / 6%);
    }
  }
}
</style>
